import { $initialAccountsStore, $profileFeatureStore, $profileStore, $remoteConfigStore, RemoteConfig } from '@lib/store';
import { AccountType, UserType, type AccountFlow, type CardFlow } from '@shared/api';
import { PaletteUtils } from './paletteUtils';

export class PermissionUtils {
  private static getPermissions = () => $profileFeatureStore.store.getState().data;
  private static getProfile = () => $profileStore.store.getState().data;
  private static getAccounts = () => $initialAccountsStore.store.getState().data;

  static viewAlertsPage = () => !!this.getPermissions()?.settings?.viewNotifications;
  static viewNotifications = () => !!this.getPermissions()?.settings?.viewNotifications;
  static deleteNotifications = () => !!this.getPermissions()?.settings?.deleteNotifications;

  static viewCurrenciesSummary = () => !!this.getPermissions()?.dashboard?.viewCurrenciesSummary;
  static viewSpendingByCurrency = () => !!this.getPermissions()?.dashboard?.viewSpendingByCurrency;
  static viewExpenseCategories = () => !!this.getPermissions()?.dashboard?.viewExpenseCategories;
  static viewMostUsedMerchants = () => !!this.getPermissions()?.dashboard?.viewMostUsedMerchants;

  static findAccountFlow = (accountFlows: AccountFlow[] | null | undefined, accountId: number | undefined) => {
    if (!accountFlows?.length || !accountId) return null;
    return accountFlows.find((accountFlow) => accountFlow.accountId === accountId) ?? null;
  };
  static getAccountFlows = () => this.getPermissions()?.accountFlows;
  static getAccountFlow = (accountId: number) => this.findAccountFlow(this.getPermissions()?.accountFlows, accountId);
  static viewAccountsPage = () => !!this.getPermissions()?.accountFlows?.length;
  static viewAccountAllTransactions = () => this.getPermissions()?.accountFlows?.some((accountFlow) => accountFlow.viewAllTransactions) ?? false;
  static viewAccountAllCards = () => this.getPermissions()?.accountFlows?.some((accountFlow) => accountFlow.viewCards) ?? false;
  static viewAccountTransactionDetails = (accountId: number) => this.getAccountFlow(accountId)?.viewTransactionDetails ?? false;
  static viewAccountAvailableBalance = (accountId: number) => this.getAccountFlow(accountId)?.viewAvailableBalance ?? false;

  static viewTransactionPage = () => this.getPermissions()?.accountFlows?.some((accountFlow) => accountFlow.viewTransactionDetails) ?? false;

  static viewPaymentsPage = () => this.getAccounts()?.some((account) => account.type === AccountType.Payment);

  static findCardFlow = (cardFlows: CardFlow[] | null | undefined, cardId: number | undefined) => {
    if (!cardFlows?.length || !cardId) return null;
    return cardFlows.find((cardFlow) => cardFlow.cardId === cardId) ?? null;
  };
  static getCardFlow = (cardId: number) => this.findCardFlow(this.getPermissions()?.cardFlows, cardId);
  static viewCardsPage = () => !!this.getPermissions()?.cardFlows?.length;
  static viewCardAllTransactions = () => this.getPermissions()?.cardFlows?.some((cardFlow) => cardFlow.viewTransactions) ?? false;
  static viewCardDetails = (cardId: number) => this.getCardFlow(cardId)?.viewCardDetails ?? false;

  static viewRewardsPage = () =>
    $remoteConfigStore.events.getValue(RemoteConfig.RewardsPage) &&
    this.getProfile()?.userType === UserType.Admin &&
    this.getProfile()?.accumulateRewards;

  static viewConciergePage = () => $remoteConfigStore.events.getValue(RemoteConfig.ConciergePage) && this.getProfile()?.userType === UserType.Admin;

  static viewUserManagementPage = () => !!this.getPermissions()?.userManagement?.viewUsers;
  static viewUserPage = () => !!this.getPermissions()?.userManagement?.viewUserProfileDetails;
  static viewUsers = () => !!this.getPermissions()?.userManagement?.viewUsers;
  static createUser = () => !!this.getPermissions()?.userManagement?.createUser;
  static updateUserProfileDetails = () => !!this.getPermissions()?.userManagement?.updateUserProfileDetails;
  static changeUserPassword = () => !!this.getPermissions()?.userManagement?.changeUserPassword;
  static lockOrUnlockUser = () => !!this.getPermissions()?.userManagement?.lockOrUnlockUser;
  static deleteUser = () => !!this.getPermissions()?.userManagement?.deleteUser;
  static manageAccountsPermissions = () => !!this.getPermissions()?.userManagement?.manageAccountsPermissions;
  static manageCardsPermissions = () => !!this.getPermissions()?.userManagement?.manageCardsPermissions;

  static viewStatementsPage = () =>
    !!(
      this.getPermissions()?.statements?.viewOrDownloadAccountStatement ||
      this.getPermissions()?.statements?.viewOrDownloadCardStatement ||
      this.getPermissions()?.statements?.viewOrDownloadConsolidatedStatement
    );
  static viewStatementsAccountTab = () => !!this.getPermissions()?.statements?.viewOrDownloadAccountStatement;
  static viewStatementsCardTab = () => !!this.getPermissions()?.statements?.viewOrDownloadCardStatement;
  static viewStatementsConsolidatedTab = () => !!this.getPermissions()?.statements?.viewOrDownloadConsolidatedStatement;
  static viewStatementPaymentsTab = () => this.viewStatementsAccountTab();
  static viewStatementCardsTab = () => this.viewStatementsAccountTab() || this.viewStatementsCardTab() || this.viewStatementsConsolidatedTab();

  static viewReportsPage = () => !!this.getPermissions()?.reports?.viewReportsSection;
  static viewReportsTransactions = () => !!this.getPermissions()?.reports?.viewTransactions;
  static viewReportsAccountBalance = () => !!this.getPermissions()?.reports?.viewAccountBalanceReport;
  static viewReportsPaymentsTab = () => this.viewReportsTransactions() || this.viewReportsAccountBalance();
  static viewReportsCardsTab = () => this.viewReportsTransactions() || this.viewReportsAccountBalance();

  static viewIntegrationsPage = () =>
    !!this.getPermissions()?.accountFlows?.some((accountFlow) => accountFlow.integrations) &&
    this.getProfile()?.userType === UserType.Admin &&
    !PaletteUtils.isPersonalPalette;

  static viewMessagesPage = () => !!this.getPermissions()?.messages?.viewOrReply;

  static viewProfilePage = () => !!this.getPermissions()?.profileSettings?.viewProfile;
  static editProfileDetails = () => !!this.getPermissions()?.profileSettings?.editDetails;
  static changeOwnPassword = () => !!this.getPermissions()?.profileSettings?.changeOwnPassword;
}
